import { string, object, ref, AnyObjectSchema, SchemaOf } from "yup";
import { setIn, ValidationErrors } from "final-form";

const WRONG_EMAIL = "Email address is not valid";
const EMAIL_REQUIRED = "Your email address is required";
const PASSWORD_LENGTH = "Password needs to be a minimum of 6 characters";
const ZIP_CODE_REQUIRED = "A zip code is required";
const ZIP_CODE_FORMAT = "Please enter a valid zip code format, e.g. 90210";
const ZIP_CODE_LENGTH = "Zip code cannot be longer than 5 numbers";
const FIELD_REQUIRED = "This field cannot be left blank";

// Regex for English characters (includes asci 32 ` ` space to 126 `~` tilde)
// This helps to prevent users from potentially typing email address like bjørn@example.com
// It DOES NOT match for email pattern; that is done with Yup's built-in
export const ENGLISH_CHARS = /^([\x20-\x7E]+)$/;

export const loginSchema = object().shape({
  login: string()
    .trim()
    .email(WRONG_EMAIL)
    .matches(ENGLISH_CHARS, {
      excludeEmptyString: true,
      message: WRONG_EMAIL,
    })
    .required(EMAIL_REQUIRED),
  password: string()
    .trim()
    .min(6, PASSWORD_LENGTH)
    .required("Your password is required"),
});

export const forgotPasswordSchema = object().shape({
  email: string()
    .trim()
    .email(WRONG_EMAIL)
    .matches(ENGLISH_CHARS, {
      excludeEmptyString: true,
      message: WRONG_EMAIL,
    })
    .required(EMAIL_REQUIRED),
});

const signUp = {
  address: string()
    .trim()
    .email(WRONG_EMAIL)
    .matches(ENGLISH_CHARS, {
      excludeEmptyString: true,
      message: WRONG_EMAIL,
    })
    .required(EMAIL_REQUIRED),
  password: string()
    .trim()
    .min(6, PASSWORD_LENGTH)
    .required("Please enter a password to use for your account"),
  passwordConfirmation: string()
    .trim()
    .oneOf([ref("password")], "Passwords do not match")
    .required("Please confirm the password you entered above"),
};

export const signupSchema = object().shape({ ...signUp });

export const signupWithAgeSchema = object().shape({
  ...signUp,
  month: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a month")
    .nullable(),
  day: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a day")
    .nullable(),
  year: object()
    .shape({
      value: string(),
      label: string(),
    })
    .required("Please select a year")
    .nullable(),
});

export const zipCodeSchema = object({
  zipCode: string()
    .trim()
    .max(5, ZIP_CODE_LENGTH)
    .matches(/\d{5}/, {
      message: ZIP_CODE_FORMAT,
    })
    .required(ZIP_CODE_REQUIRED),
});

export type GiftType = {
  gifteeEmail?: string;
  gifteeMessage?: string;
  giftorName?: string;
};

export const giftSchema: SchemaOf<{ giftForm: GiftType }> = object({
  giftForm: object().shape({
    gifteeEmail: string().trim().email(WRONG_EMAIL).required(FIELD_REQUIRED),
    gifteeMessage: string(),
    giftorName: string().required(FIELD_REQUIRED),
  }),
});

/* https://gist.github.com/manzoorwanijk/5993a520f2ac7890c3b46f70f6818e0a */
export const validateWithYup =
  (schema: AnyObjectSchema) =>
  async (values: unknown): Promise<ValidationErrors | undefined> => {
    try {
      await schema.validate(values, { abortEarly: false });
      // using explicit any here because the `schema` above is an AnyObjectSchema
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const errors = err?.inner?.reduce(
        (
          formError: Record<string, unknown>,
          innerError: { path: string; message: string }
        ) => {
          return setIn(formError, innerError.path, innerError.message);
        },
        {}
      );

      return errors;
    }
  };
